/* =============================================================================
   CORE / SHRINKAGE — Soll-Ist-Schwund (Phase E5, S164)
   =============================================================================
   PURE Helfer für den Schwund-Report (Spiegel der SQL-RPC shrinkage_report,
   Migration phase_123). Bewegungsbasiert aus dem stock_movements-Ledger:

     Soll-Verbrauch = |sale_out|                         (laut Verkäufen)
     Ist-Verbrauch  = opening + purchase_in − closing     (tatsächlich abgegangen)
     Schwund        = Ist − Soll = −(waste+correction+inventory)
     Schwund-%      = Schwund / Soll × 100  (nur Soll > 0)
     €-Verlust      = Schwund × raw_per_base (purchase_price/purchase_qty; null = nicht bewertet)

   Negativer Schwund (mehr Bestand als erwartet, z. B. Überlieferung) ist gültig und
   wird sachlich dargestellt. €-Format/„—" konsistent zum Lagerwert (formatStockValue).
============================================================================ */

import { formatStockValue } from './stock.js';

/** Soll-Verbrauch aus der (negativen) Verkaufs-Summe = |sale_out|. */
export function sollFromSale(saleOut) {
  return Math.abs(Number(saleOut) || 0);
}

/** Ist-Verbrauch = opening + purchase_in − closing. */
export function istConsumption(opening, purchaseBase, closing) {
  return (Number(opening) || 0) + (Number(purchaseBase) || 0) - (Number(closing) || 0);
}

/** Schwund = Ist − Soll (kann negativ sein). */
export function shrinkBase(ist, soll) {
  return (Number(ist) || 0) - (Number(soll) || 0);
}

/** Schwund-% = Schwund / Soll × 100; null wenn Soll ≤ 0 (kein sinnvoller Bezug). */
export function shrinkPct(shrink, soll) {
  const s = Number(soll);
  if (!(s > 0)) return null;
  return Number(shrink) / s * 100;
}

/** €-Verlust = Schwund × roher Einkaufspreis je Basiseinheit; null ohne Preis. */
export function shrinkValue(shrink, rawPerBase) {
  if (rawPerBase == null || !isFinite(Number(rawPerBase))) return null;
  return Number(shrink) * Number(rawPerBase);
}

/** Σ €-Verlust über Report-Zeilen (null/unbewertete zählen 0). Spiegel shrinkage_totals. */
export function aggregateShrinkValue(rows) {
  return (Array.isArray(rows) ? rows : []).reduce((sum, r) => {
    const v = r && r.shrinkValueEur;
    return sum + (v != null && isFinite(Number(v)) ? Number(v) : 0);
  }, 0);
}

/** Anzahl Zutaten ohne Einkaufspreis (rawPerBase == null) → „nicht bewertet". */
export function countUnvalued(rows) {
  return (Array.isArray(rows) ? rows : []).filter(r => !r || r.rawPerBase == null).length;
}

/** €-Wert formatieren (deutsch, 2 NK, Vorzeichen erhalten). null/NaN → „—". */
export function formatShrinkValue(n) {
  return formatStockValue(n);
}

/** Schwund-% formatieren (1 NK, Vorzeichen). null/NaN → „—". */
export function formatShrinkPct(n) {
  if (n == null || !isFinite(Number(n))) return '—';
  const v = Number(n);
  return (v > 0 ? '+' : '') + v.toFixed(1).replace('.', ',') + ' %';
}

/**
 * Zeitraum „laufender Monat" als ISO-Datumsstrings {from:'YYYY-MM-01', to:'YYYY-MM-<last>'}.
 * Referenzdatum injizierbar (Test-stabil; Default = heute zur Laufzeit im Aufruf gesetzt).
 */
export function monthRange(ref) {
  const d = ref instanceof Date ? ref : new Date(ref);
  const y = d.getFullYear(), m = d.getMonth();
  const pad = n => String(n).padStart(2, '0');
  const first = `${y}-${pad(m + 1)}-01`;
  const lastDay = new Date(y, m + 1, 0).getDate();
  const last = `${y}-${pad(m + 1)}-${pad(lastDay)}`;
  return { from: first, to: last };
}
